/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIConnectorRatingResponseInterface,
  ConnectorRatingAllInterface,
  ConnectorRatingInitialProperties,
  ConnectorRatingInterface,
  RequestConnectorRatingInterface,
} from '../../interface/connector-rating.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
  ScopeListProperties,
} from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import connectorApi from '../../utils/api/connector-rating';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Fetch all connector ratings
 */
export const fetchAllConnectorRatings = createAsyncThunk<
  APIConnectorRatingResponseInterface,
  RequestConnectorRatingInterface
>(
  'connector-rating/fetchAllConnectorRatings',

  async (data: RequestConnectorRatingInterface, { dispatch }) => {
    try {
      const response = await connectorApi.fetchConnectorRatings(removeEmptyKeys(data));

      return {
        ...response.data,

        results: response.data.results.map((connectorRating: ConnectorRatingInterface) => ({
          ...connectorRating,

          createdAt: ConvertUTCtoDate(connectorRating.createdAt || ''),

          updatedAt: ConvertUTCtoDate(connectorRating.updatedAt || ''),
        })),
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Add connector rating
 */
export const addConnectorRating = createAsyncThunk(
  'connector-rating/addConnectorRating',

  async (
    data: {
      connectorRatingName: string;
      type: string;
      maxPowerOutput: string;
      status: boolean;
    },

    { dispatch },
  ) => {
    const response = await connectorApi
      .createConnectorRating(data)

      .then((response1: any) => response1)

      .catch((error: any) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );

    return response.data;
  },
);

/**
 * Fetch connector rating filters
 */
export const fetchConnectorRatingFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'connector-rating/fetchConnectorRatingFilters',

  async (_, { dispatch }) => {
    try {
      const response = await connectorApi.fetchConnectorRatingFilterList();

      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);

      return updatedResponse;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        }),
      );

      throw error;
    }
  },
);

/**
 * Edit connector rating
 */
export const editConnectorRating = createAsyncThunk(
  'connector-rating/editConnectorRating',

  async (
    data: {
      id: string;
      connectorRatingName: string;
      type: string;
      maxPowerOutput: string;
      status: boolean;
    },

    { dispatch },
  ) => {
    const response = await connectorApi
      .editConnectorRating(data)

      .then((response1: any) => response1)

      .catch((error: any) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );

    return response.data;
  },
);

/**
 * Delete connector rating
 */
export const deleteConnectorRating = createAsyncThunk(
  'connector-rating/deleteConnectorRating',
  async (id: string, { dispatch }) => {
    try {
      const response = await connectorApi.deleteConnectorRating(id);
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to delete connector rating',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Fetch all connector list
 */
export const fetchAllConnectorRating = createAsyncThunk(
  'connector-rating/fetchAllConnectorRating',
  async (_, { dispatch }) => {
    try {
      const response = await connectorApi.fetchConnectorRatingAllList();
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to delete connector rating',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Fetch all connector list
 */
export const fetchConnectorScopList = createAsyncThunk(
  'connector-rating/fetchConnectorScopList',
  async (data: any, { dispatch }) => {
    try {
      const response = await connectorApi.fetchConnectorScopList(data.scopeId);
      return { data: response.data, scope: data.scope };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to delete connector rating',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Available filters
 */
const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state
 */
const initialState: ConnectorRatingInitialProperties = {
  connectorRatings: [],

  connectorRatingsAll: [],

  scopeList: {},

  limit: DefaultApiParameters.limit,

  page: DefaultApiParameters.page,

  totalPages: 0,

  totalResults: 0,

  loading: false,

  filters: availableFilters,

  filterListLoading: false,

  isFilterUpdated: false,

  sortBy: '',

  sortField: '',
};

/**
 * Connector rating slice
 */
export const connectorRatingSlice = createSlice({
  name: 'connector-rating',

  initialState,

  reducers: {
    /**
     * Update connector rating filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateConnectorRatingFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };

      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );

        state.filters = updatedCommonFilter;

        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);

        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);

        state.isFilterUpdated = true;
      }
    },

    /**
     * Disable filter applied
     * @param {any} state The current slice state.
     */
    disableConnectorRatingFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },

    /**
     * Set sort data
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSortData: (
      state,
      action: PayloadAction<{
        sortField: string;
        sortBy: 'asc' | 'desc';
      }>,
    ) => {
      state.sortField = action.payload.sortField;

      state.sortBy = action.payload.sortBy;
    },
  },

  /**
   * Extra reducers
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<ConnectorRatingInitialProperties>) => {
    builder
      .addCase(
        fetchAllConnectorRatings.pending,

        (state: ConnectorRatingInitialProperties) => {
          state.loading = true;
        },
      )

      .addCase(
        fetchAllConnectorRatings.fulfilled,

        (
          state: ConnectorRatingInitialProperties,
          action: PayloadAction<APIConnectorRatingResponseInterface>,
        ) => {
          state.loading = false;

          state.connectorRatings = [...action.payload.results];

          state.limit = action.payload.limit;

          state.page = action.payload.page;

          state.totalPages = action.payload.totalPages;

          state.totalResults = action.payload.totalResults;
        },
      )

      .addCase(
        fetchAllConnectorRatings.rejected,

        (state: ConnectorRatingInitialProperties) => {
          state.loading = false;
        },
      );

    builder
      .addCase(
        fetchConnectorRatingFilters.pending,

        (state) => {
          state.filterListLoading = true;
        },
      )

      .addCase(
        fetchConnectorRatingFilters.fulfilled,

        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;

          state.filters = [...(state.filters || []), ...action.payload];
        },
      )

      .addCase(
        fetchConnectorRatingFilters.rejected,

        (state) => {
          state.filterListLoading = false;
        },
      );

    builder
      .addCase(fetchAllConnectorRating.pending, () => {})
      .addCase(
        fetchAllConnectorRating.fulfilled,
        (state, action: PayloadAction<ConnectorRatingAllInterface[]>) => {
          state.connectorRatingsAll = action.payload || [];
        },
      )
      .addCase(fetchAllConnectorRating.rejected, () => {});

    builder
      .addCase(fetchConnectorScopList.pending, () => {})
      .addCase(
        fetchConnectorScopList.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: ScopeListProperties[];
            scope?: any;
          }>,
        ) => {
          const scope = action.payload?.scope ?? 'country';

          if (!state.scopeList) {
            state.scopeList = {};
          }

          (state.scopeList as Record<string, any>)[scope] = action.payload.data.map(
            (rating: any) => ({
              label: rating.scopeName,
              value: rating.scopeId,
              childType: rating.childType,
            }),
          );
        },
      )
      .addCase(fetchConnectorScopList.rejected, () => {});
  },
});

/**
 * Export actions
 */
export const { updateConnectorRatingFilters, disableConnectorRatingFilterApplied, setSortData } =
  connectorRatingSlice.actions;

/**
 * Export reducer
 */
export default connectorRatingSlice.reducer;
